import { formatMoney, useReportCurrency } from "@/profitability/currency";
import { isNumericType } from "@/profitability/missing";
import { showValue } from "@/profitability/missing-value";
import { formatCell } from "@/profitability/records/table-values";
import type { TemplateSetting } from "@/profitability/settings/types";
import { formatSetting } from "@/profitability/settings/values";
import type { ReportColumn, ReportValue } from "@/profitability/types";
import { parseFigure } from "./sourced";

const CENTS = { maximumFractionDigits: 2 };
const MONEY = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

/**
 * Writes a figure's values as the dialog shows them: money and numbers to
 * the cent, because the report rounds money to whole units and a figure
 * that differs by cents must not read as the same; a setting as the
 * Settings page writes it.
 */
export const useFigureFormat = () => {
  const currency = useReportCurrency();
  const value = (
    item: ReportValue | undefined,
    column: ReportColumn | null,
  ) => {
    if (!column) return String(item ?? "");
    const figure = isNumericType(column.type)
      ? parseFigure(item ?? null)
      : null;
    if (figure !== null && column.type === "currency")
      return formatMoney(figure, currency, MONEY);
    if (figure !== null && column.type === "number")
      return figure.toLocaleString(undefined, CENTS);
    return showValue(formatCell(figure ?? item ?? null, column, currency));
  };
  const setting = (
    item: ReportValue | undefined,
    declared: Pick<TemplateSetting, "type" | "value_type"> | undefined,
  ) =>
    declared && (typeof item === "number" || typeof item === "string")
      ? formatSetting(declared, item, currency)
      : typeof item === "number"
        ? item.toLocaleString(undefined, { maximumFractionDigits: 4 })
        : String(item ?? "");
  return { value, setting };
};
